/* eslint-disable import/no-anonymous-default-export */
import useFutureTickets from "../../../MyAccount/Reservations/hooks/useReservation";
import { useMemo, useState } from "react";
import { useLocation } from "react-router";
import { isAfter } from "date-fns";
import { parseAccountIdsFromSearch } from "../../../../../utils/myAccountTabUrl";

const ITEMS_PER_PAGE = 8;

export default () => {
  const location = useLocation();
  const accountIds = parseAccountIdsFromSearch(location.search);
  const [showPastReservation, setShowPastReservation] = useState(false);
  const [displayedUpcomingCount, setDisplayedUpcomingCount] = useState(ITEMS_PER_PAGE);
  const [displayedPastCount, setDisplayedPastCount] = useState(ITEMS_PER_PAGE);
  const { tickets, loading, refreshTickets } = useFutureTickets(accountIds);

  // Sort tickets by their exercises' start times
  const sortedTickets = useMemo(() => {
    if (!tickets || tickets.length === 0) return [];

    // Create a copy to avoid mutating original data
    return [...tickets].map((ticket) => {
      // Sort exercises within each ticket from earliest to latest
      const sortedExercises = [...ticket.exercises].sort((a, b) => {
        // Handle cases where startTime might be undefined
        if (!a.startTime) return 1; // Undefined dates go last
        if (!b.startTime) return -1;
        return a.startTime - b.startTime;
      });

      return { ...ticket, exercises: sortedExercises };
    });
  }, [tickets]);

  // Zeskanowana obecność (Y/U) to fakt niezależny od zegara — check-in przy wejściu może
  // zajść przed formalną godziną startu zajęć. Bez tego (i przy samym porównaniu dnia
  // kalendarzowego, jak poprzednio) rezerwacja mogła wisieć w "Nadchodzące" z plakietką
  // "Minęło" wewnątrz — nagłówek sekcji zaprzeczał własnej zawartości.
  const isExerciseUpcoming = (exercise: { startTime: number; status?: string }): boolean => {
    const isAttended = exercise.status === "U" || exercise.status === "Y";
    if (isAttended) return false;
    return isAfter(new Date(exercise.startTime), new Date());
  };

  const hasValidTicket = sortedTickets.some((ticket) =>
    ticket.exercises.some((exercise) => !isExerciseUpcoming(exercise) && ticket.ticket === false)
  );

  // Create flat array of upcoming reservations for sorting
  const upcomingReservations = sortedTickets.flatMap((ticket) =>
    ticket.exercises
      .filter((exercise) => exercise.startTime && isExerciseUpcoming(exercise))
      .map((exercise) => ({ ticket, exercise }))
  );

  // Sort upcoming reservations by start time (earliest first)
  upcomingReservations.sort((a, b) => {
    return a.exercise.startTime - b.exercise.startTime;
  });

  // Create flat array of past reservations
  const pastReservations = sortedTickets.flatMap((ticket) =>
    ticket.exercises
      .filter((exercise) => exercise.startTime && !isExerciseUpcoming(exercise))
      .map((exercise) => ({ ticket, exercise }))
  );

  // Sort past reservations by start time (latest first)
  pastReservations.sort((a, b) => {
    return b.exercise.startTime - a.exercise.startTime;
  });

  return {
    upcomingReservations,
    pastReservations,
    hasValidTicket,
    loading,
    refreshTickets,
    sortedTickets,
    showPastReservation,
    setShowPastReservation,
    displayedUpcomingCount,
    setDisplayedUpcomingCount,
    displayedPastCount,
    setDisplayedPastCount,
    ITEMS_PER_PAGE,
  };
};